import { useState, useRef, useEffect } from "react";
import MainLayout from "@/components/feature/MainLayout";
import { meterDevices, meterAIInsights } from "@/mocks/meterDevice";

const statusConfig: Record<string, { color: string; bg: string; dot: string }> = {
  "正常": { color: "text-green-600", bg: "bg-green-50", dot: "bg-green-500" },
  "告警": { color: "text-red-600", bg: "bg-red-50", dot: "bg-red-500" },
  "离线": { color: "text-gray-500", bg: "bg-gray-100", dot: "bg-gray-400" },
  "待校验": { color: "text-amber-600", bg: "bg-amber-50", dot: "bg-amber-500" },
};

const aiReportText = `【AI表具健康分析报告 - 2026年4月】

一、设备整体状况
当前在管表具共 ${meterDevices.length} 台，正常运行 ${meterDevices.filter(d => d.status === "正常").length} 台，告警 ${meterDevices.filter(d => d.status === "告警").length} 台，离线 ${meterDevices.filter(d => d.status === "离线").length} 台，待校验 ${meterDevices.filter(d => d.status === "待校验").length} 台。

二、告警设备分析
AI检测到以下异常情况：
• 燃气泄漏告警：2台，需立即派工处理
• 压力异常：1台，建议检查管网压力
• 电池低电量：3台，建议在下次上门时更换
• 信号弱：2台，建议检查NB-IoT网络覆盖

三、校验到期预警
未来30天内有 4 台表具需要进行周期校验，建议提前安排校验计划，避免超期使用。

四、设备寿命预测
AI基于设备运行数据分析：
• 预计未来6个月需更换设备：8台
• 建议提前采购备用表具：10台
• 重点关注安装超过8年的老旧设备

五、优化建议
1. 对告警设备优先派工，24小时内完成处理
2. 建立设备预防性维护计划，降低故障率
3. 推进老旧机械表改造为智能表，提升数据采集效率`;

export default function MeterDevicePage() {
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("全部");
  const [selected, setSelected] = useState<typeof meterDevices[0] | null>(null);
  const [addModal, setAddModal] = useState(false);
  const [activeTab, setActiveTab] = useState<"list" | "stats" | "report">("list");
  const [aiReport, setAiReport] = useState("");
  const [generating, setGenerating] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const trendRef = useRef<HTMLCanvasElement>(null);

  const statuses = ["全部", "正常", "告警", "离线", "待校验"];
  const filtered = meterDevices.filter(d => {
    const matchStatus = filterStatus === "全部" || d.status === filterStatus;
    const matchSearch = !search || d.userName.includes(search) || d.id.includes(search) || d.address.includes(search);
    return matchStatus && matchSearch;
  });

  const stats = {
    total: meterDevices.length,
    normal: meterDevices.filter(d => d.status === "正常").length,
    alarm: meterDevices.filter(d => d.status === "告警").length,
    offline: meterDevices.filter(d => d.status === "离线").length,
    calibration: meterDevices.filter(d => d.status === "待校验").length,
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || activeTab !== "stats") return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const cx = w / 2;
    const cy = h / 2;
    const r = Math.min(w, h) / 2 - 20;
    const data = [
      { label: "正常", value: stats.normal, color: "#22c55e" },
      { label: "告警", value: stats.alarm, color: "#ef4444" },
      { label: "离线", value: stats.offline, color: "#9ca3af" },
      { label: "待校验", value: stats.calibration, color: "#f59e0b" },
    ];
    const total = data.reduce((s, d) => s + d.value, 0);
    let startAngle = -Math.PI / 2;

    data.forEach(d => {
      const angle = (d.value / total) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r, startAngle, startAngle + angle);
      ctx.closePath();
      ctx.fillStyle = d.color;
      ctx.fill();
      ctx.strokeStyle = "white";
      ctx.lineWidth = 2;
      ctx.stroke();

      const midAngle = startAngle + angle / 2;
      const lx = cx + (r * 0.65) * Math.cos(midAngle);
      const ly = cy + (r * 0.65) * Math.sin(midAngle);
      ctx.fillStyle = "white";
      ctx.font = "bold 11px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(`${((d.value / total) * 100).toFixed(0)}%`, lx, ly + 4);

      startAngle += angle;
    });

    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.45, 0, Math.PI * 2);
    ctx.fillStyle = "white";
    ctx.fill();
    ctx.fillStyle = "#374151";
    ctx.font = "bold 14px sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(total.toString(), cx, cy - 4);
    ctx.font = "10px sans-serif";
    ctx.fillStyle = "#9ca3af";
    ctx.fillText("总台数", cx, cy + 12);
  }, [activeTab, stats]);

  useEffect(() => {
    const canvas = trendRef.current;
    if (!canvas || activeTab !== "stats") return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const months = ["10月", "11月", "12月", "1月", "2月", "3月", "4月"];
    const alarmTrend = [8, 6, 9, 5, 7, 4, 3];
    const offlineTrend = [12, 10, 8, 9, 7, 6, 5];
    const maxV = Math.max(...alarmTrend, ...offlineTrend) + 2;
    const stepX = (w - 60) / (months.length - 1);

    const drawLine = (data: number[], color: string) => {
      const points = data.map((v, i) => ({
        x: 30 + i * stepX,
        y: h - 30 - ((v / maxV) * (h - 50)),
      }));
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      points.forEach(p => ctx.lineTo(p.x, p.y));
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.stroke();
      points.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, 3.5, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
      });
    };

    drawLine(alarmTrend, "#ef4444");
    drawLine(offlineTrend, "#9ca3af");

    months.forEach((m, i) => {
      ctx.fillStyle = "rgba(80,80,80,0.7)";
      ctx.font = "9px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(m, 30 + i * stepX, h - 12);
    });
  }, [activeTab]);

  const generateReport = () => {
    setGenerating(true);
    setAiReport("");
    const text = customPrompt
      ? `【自定义表具分析报告】\n\n根据您的要求「${customPrompt}」：\n\n${aiReportText}`
      : aiReportText;
    let i = 0;
    const timer = setInterval(() => {
      if (i >= text.length) { clearInterval(timer); setGenerating(false); return; }
      setAiReport(text.slice(0, i + 4));
      i += 4;
    }, 25);
  };

  return (
    <MainLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">表具管理</h1>
            <p className="text-sm text-gray-500 mt-0.5">设备台账 · 状态监控 · AI智能诊断 · 分析报告</p>
          </div>
          <div className="flex gap-2">
            <button className="flex items-center gap-2 border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">
              <i className="ri-download-line"></i>导出台账
            </button>
            <button onClick={() => setAddModal(true)} className="flex items-center gap-2 bg-[#E8531A] text-white px-4 py-2 rounded-lg text-sm hover:bg-[#d44a15] cursor-pointer whitespace-nowrap">
              <i className="ri-add-line"></i>新增表具
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-3">
          {[
            { label: "设备总数", value: stats.total, color: "text-gray-800", bg: "bg-gray-50", icon: "ri-cpu-line" },
            { label: "正常运行", value: stats.normal, color: "text-green-600", bg: "bg-green-50", icon: "ri-checkbox-circle-line" },
            { label: "告警设备", value: stats.alarm, color: "text-red-600", bg: "bg-red-50", icon: "ri-alarm-warning-line" },
            { label: "离线设备", value: stats.offline, color: "text-gray-500", bg: "bg-gray-100", icon: "ri-wifi-off-line" },
            { label: "待校验", value: stats.calibration, color: "text-amber-600", bg: "bg-amber-50", icon: "ri-time-line" },
          ].map((s, i) => (
            <div key={i} className={`${s.bg} rounded-xl p-4 flex items-center gap-3`}>
              <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-white">
                <i className={`${s.icon} ${s.color} text-lg`}></i>
              </div>
              <div>
                <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-xs text-gray-500">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* AI Insights */}
        <div className="bg-gradient-to-r from-[#0f1729] to-[#1a2540] rounded-xl p-4 border border-white/10">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-[#E8531A]/20">
              <i className="ri-brain-line text-[#E8531A] text-sm"></i>
            </div>
            <span className="text-white text-sm font-semibold">AI 表具智能诊断</span>
            <span className="flex items-center gap-1 text-xs text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full ml-auto">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse inline-block"></span>
              实时分析
            </span>
          </div>
          <div className="grid grid-cols-4 gap-3">
            {meterAIInsights.map((item, i) => (
              <div key={i} className={`${item.level === "high" ? "bg-red-50" : "bg-amber-50"} rounded-xl p-3`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-gray-800">{item.title}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${item.level === "high" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-600"}`}>
                    {item.level === "high" ? "高优先" : "中优先"}
                  </span>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
          {[
            { key: "list", label: "设备台账", icon: "ri-list-check" },
            { key: "stats", label: "统计分析", icon: "ri-pie-chart-line" },
            { key: "report", label: "AI分析报告", icon: "ri-robot-2-line" },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key as typeof activeTab)}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${activeTab === t.key ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            >
              <i className={t.icon}></i>{t.label}
            </button>
          ))}
        </div>

        {activeTab === "list" && (
          <>
            <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3 flex-wrap">
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                {statuses.map(s => (
                  <button
                    key={s}
                    onClick={() => setFilterStatus(s)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${filterStatus === s ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <div className="relative flex-1 min-w-48">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="搜索设备编号、用户名、地址..."
                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50"
                />
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    {["设备编号", "用户姓名", "安装地址", "表具类型", "品牌型号", "当前读数", "本月用量", "余额(元)", "信号", "电量", "状态", "操作"].map(h => (
                      <th key={h} className="text-left text-xs font-medium text-gray-500 px-3 py-3 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map(item => {
                    const cfg = statusConfig[item.status];
                    return (
                      <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 py-3 text-xs font-mono text-gray-500">{item.id}</td>
                        <td className="px-3 py-3 text-sm font-medium text-gray-800 whitespace-nowrap">{item.userName}</td>
                        <td className="px-3 py-3 text-xs text-gray-500 max-w-36 truncate">{item.address}</td>
                        <td className="px-3 py-3">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-orange-50 text-[#E8531A]">{item.type}</span>
                        </td>
                        <td className="px-3 py-3 text-xs text-gray-600 whitespace-nowrap">{item.brand} {item.model}</td>
                        <td className="px-3 py-3 text-sm font-bold text-gray-800">{item.reading.toLocaleString()} m³</td>
                        <td className="px-3 py-3 text-sm text-gray-700">{item.monthUsage} m³</td>
                        <td className="px-3 py-3">
                          <span className={`text-sm font-bold ${item.balance <= 20 ? "text-red-600" : item.balance <= 50 ? "text-amber-600" : "text-gray-800"}`}>
                            {item.balance.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1">
                            <div className={`w-1.5 h-1.5 rounded-full ${item.signal > 80 ? "bg-green-500" : item.signal > 50 ? "bg-amber-500" : item.signal === 0 ? "bg-gray-300" : "bg-red-500"}`}></div>
                            <span className="text-xs text-gray-600">{item.signal}%</span>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1">
                            <div className="w-12 bg-gray-100 rounded-full h-1.5">
                              <div className={`h-1.5 rounded-full ${item.batteryLevel > 50 ? "bg-green-500" : item.batteryLevel > 20 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${item.batteryLevel}%` }} />
                            </div>
                            <span className="text-xs text-gray-500">{item.batteryLevel}%</span>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1.5">
                            <div className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}></div>
                            <span className={`text-xs font-medium ${cfg.color}`}>{item.status}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex gap-2">
                            <button onClick={() => setSelected(item)} className="text-xs text-[#E8531A] hover:underline cursor-pointer whitespace-nowrap">详情</button>
                            <button className="text-xs text-gray-500 hover:underline cursor-pointer whitespace-nowrap">指令</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === "stats" && (
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-pie-chart-line text-[#E8531A]"></i>
                <span className="text-sm font-semibold text-gray-800">设备状态分布</span>
              </div>
              <canvas ref={canvasRef} width={300} height={220} className="w-full" />
              <div className="mt-3 grid grid-cols-2 gap-2">
                {[
                  { label: "正常", value: stats.normal, color: "bg-green-500" },
                  { label: "告警", value: stats.alarm, color: "bg-red-500" },
                  { label: "离线", value: stats.offline, color: "bg-gray-400" },
                  { label: "待校验", value: stats.calibration, color: "bg-amber-500" },
                ].map((d, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${d.color}`}></div>
                    <span className="text-xs text-gray-500">{d.label}</span>
                    <span className="text-xs font-bold text-gray-700 ml-auto">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-line-chart-line text-[#E8531A]"></i>
                <span className="text-sm font-semibold text-gray-800">告警/离线趋势（近7月）</span>
              </div>
              <canvas ref={trendRef} width={400} height={180} className="w-full" />
              <div className="mt-3 flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1.5"><div className="w-3 h-1.5 rounded bg-red-500"></div><span className="text-gray-500">告警</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-1.5 rounded bg-gray-400"></div><span className="text-gray-500">离线</span></div>
              </div>
            </div>
            <div className="col-span-2 bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-calendar-check-line text-[#E8531A]"></i>
                <span className="text-sm font-semibold text-gray-800">近期校验计划</span>
              </div>
              <div className="grid grid-cols-4 gap-3">
                {[
                  { id: "MTR-2024-001", user: "张建国", date: "2026-04-15", type: "IC卡智能表", status: "待安排" },
                  { id: "MTR-2024-008", user: "李秀梅", date: "2026-04-20", type: "NB-IoT智能表", status: "已预约" },
                  { id: "MTR-2024-015", user: "王大明", date: "2026-04-28", type: "IC卡智能表", status: "待安排" },
                  { id: "MTR-2024-022", user: "陈小红", date: "2026-05-05", type: "普通机械表", status: "待安排" },
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-mono text-gray-500">{item.id}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${item.status === "已预约" ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"}`}>{item.status}</span>
                    </div>
                    <p className="text-sm font-medium text-gray-800">{item.user}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{item.type}</p>
                    <p className="text-xs text-[#E8531A] mt-1 font-medium">{item.date}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "report" && (
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <i className="ri-robot-2-line text-[#E8531A]"></i>
                <span className="text-sm font-semibold text-gray-800">AI表具智能分析报告</span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  value={customPrompt}
                  onChange={e => setCustomPrompt(e.target.value)}
                  placeholder="自定义分析要求（可选）..."
                  className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 w-56 focus:outline-none focus:border-[#E8531A]"
                />
                <button
                  onClick={generateReport}
                  disabled={generating}
                  className="flex items-center gap-1.5 text-xs bg-[#E8531A] text-white px-4 py-1.5 rounded-lg cursor-pointer hover:bg-[#d44a15] disabled:opacity-60 whitespace-nowrap"
                >
                  <i className={`${generating ? "ri-loader-4-line animate-spin" : "ri-magic-line"}`}></i>
                  {generating ? "AI生成中..." : "生成分析报告"}
                </button>
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-5 min-h-64 max-h-96 overflow-y-auto">
              {aiReport ? (
                <pre className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap font-sans">{aiReport}{generating && <span className="animate-pulse text-[#E8531A]">|</span>}</pre>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center mb-4">
                    <i className="ri-robot-2-line text-[#E8531A] text-2xl"></i>
                  </div>
                  <p className="text-sm font-medium text-gray-600 mb-1">AI表具分析报告</p>
                  <p className="text-xs text-gray-400">点击「生成分析报告」，AI将自动分析所有表具数据</p>
                  <p className="text-xs text-gray-400 mt-1">也可在输入框中填写自定义分析要求</p>
                </div>
              )}
            </div>
            {aiReport && !generating && (
              <div className="flex gap-2 mt-4">
                <button className="flex-1 border border-[#E8531A] text-[#E8531A] py-2 rounded-lg text-sm cursor-pointer hover:bg-orange-50 whitespace-nowrap">
                  <i className="ri-download-line mr-1"></i>下载PDF报告
                </button>
                <button className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                  <i className="ri-mail-send-line mr-1"></i>发送邮件
                </button>
                <button onClick={() => setAiReport("")} className="px-4 border border-gray-200 text-gray-600 py-2 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                  重新生成
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Device Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl p-6 w-[620px] max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-base font-bold text-gray-900">表具详情 · {selected.id}</h3>
                <p className="text-xs text-gray-400 mt-0.5">{selected.userName} · {selected.address}</p>
              </div>
              <button onClick={() => setSelected(null)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>

            {selected.alarmType && (
              <div className="bg-red-50 border border-red-100 rounded-xl p-3 mb-4 flex items-center gap-2">
                <i className="ri-alarm-warning-line text-red-500"></i>
                <span className="text-sm text-red-600 font-medium">告警：{selected.alarmType}</span>
              </div>
            )}

            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                { label: "当前读数", value: `${selected.reading.toLocaleString()} m³`, icon: "ri-dashboard-line" },
                { label: "本月用量", value: `${selected.monthUsage} m³`, icon: "ri-bar-chart-line" },
                { label: "账户余额", value: `¥${selected.balance.toFixed(2)}`, icon: "ri-money-cny-circle-line" },
                { label: "当前压力", value: `${selected.pressure} MPa`, icon: "ri-speed-line" },
                { label: "当前温度", value: `${selected.temp}°C`, icon: "ri-temp-hot-line" },
                { label: "信号强度", value: `${selected.signal}%`, icon: "ri-signal-wifi-line" },
              ].map((item, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <i className={`${item.icon} text-gray-400 text-xs`}></i>
                    <span className="text-xs text-gray-400">{item.label}</span>
                  </div>
                  <div className="text-sm font-bold text-gray-800">{item.value}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-400 mb-2">设备信息</div>
                <div className="space-y-1.5">
                  {[
                    { k: "品牌型号", v: `${selected.brand} ${selected.model}` },
                    { k: "表具类型", v: selected.type },
                    { k: "安装日期", v: selected.installDate },
                    { k: "铅封状态", v: selected.sealStatus },
                  ].map((row, i) => (
                    <div key={i} className="flex justify-between text-xs">
                      <span className="text-gray-500">{row.k}</span>
                      <span className="font-medium text-gray-700">{row.v}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-400 mb-2">校验信息</div>
                <div className="space-y-1.5">
                  {[
                    { k: "上次校验", v: selected.lastCalib },
                    { k: "下次校验", v: selected.nextCalib },
                    { k: "电池电量", v: `${selected.batteryLevel}%` },
                    { k: "当前状态", v: selected.status },
                  ].map((row, i) => (
                    <div key={i} className="flex justify-between text-xs">
                      <span className="text-gray-500">{row.k}</span>
                      <span className={`font-medium ${row.k === "当前状态" ? statusConfig[selected.status]?.color : "text-gray-700"}`}>{row.v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-100">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-brain-line text-[#E8531A] text-sm"></i>
                <span className="text-sm font-semibold text-gray-800">AI 设备健康诊断</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-green-500 text-sm mt-0.5"></i>
                  <p className="text-xs text-gray-600">设备综合健康评分 {selected.status === "正常" ? "92" : selected.status === "告警" ? "65" : "45"}分，{selected.status === "正常" ? "运行状态良好" : "需要关注处理"}</p>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-information-line text-teal-500 text-sm mt-0.5"></i>
                  <p className="text-xs text-gray-600">
                    {selected.batteryLevel < 30 ? "电池电量不足，建议在下次上门服务时更换电池" : `电池状态良好，预计可使用${Math.floor(selected.batteryLevel / 5)}个月`}
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <i className={`${selected.alarmType ? "ri-alert-line text-amber-500" : "ri-shield-check-line text-green-500"} text-sm mt-0.5`}></i>
                  <p className="text-xs text-gray-600">
                    {selected.alarmType ? `检测到异常：${selected.alarmType}，建议优先处理` : "设备运行参数正常，无异常告警"}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <button className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">远程阀控</button>
              <button className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">下发气价</button>
              <button className="flex-1 bg-[#E8531A] text-white py-2 rounded-lg text-sm cursor-pointer hover:bg-[#d44a15] whitespace-nowrap">派工维修</button>
            </div>
          </div>
        </div>
      )}

      {addModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setAddModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-[500px]" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-gray-900 mb-4">新增表具档案</h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: "设备编号", placeholder: "MTR-XXXX-XXX" },
                { label: "用户姓名", placeholder: "关联用户姓名" },
                { label: "品牌", placeholder: "如：金卡智能" },
                { label: "型号", placeholder: "如：JK-G2.5" },
                { label: "安装日期", placeholder: "YYYY-MM-DD", type: "date" },
                { label: "初始读数", placeholder: "0.00", type: "number" },
              ].map((f, i) => (
                <div key={i}>
                  <label className="text-xs text-gray-500 mb-1 block">{f.label}</label>
                  <input type={f.type || "text"} placeholder={f.placeholder} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]" />
                </div>
              ))}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">表具类型</label>
                <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]">
                  <option>IC卡智能表</option>
                  <option>物联网智能表</option>
                  <option>普通机械表</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">安装地址</label>
                <input type="text" placeholder="详细安装地址" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]" />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setAddModal(false)} className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">取消</button>
              <button onClick={() => setAddModal(false)} className="flex-1 bg-[#E8531A] text-white py-2 rounded-lg text-sm cursor-pointer hover:bg-[#d44a15] whitespace-nowrap">确认新增</button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
